const sectionSelector = '.fill-shop-the-look';
const configuratorSelector = '[data-fill-look-configurator]';
const openSelector = '[data-fill-look-open]';
const closeSelector = '[data-fill-look-close]';
const dataSelector = '[data-fill-look-data]';
const formSelector = '[data-fill-look-form]';
const rowsSelector = '[data-fill-look-rows]';
const errorSelector = '[data-fill-look-error]';
const statusSelector = '[data-fill-look-status]';
const submitSelector = '[data-fill-look-submit]';
const submitTextSelector = '[data-fill-look-submit-text]';
const countSelector = '[data-fill-look-count]';
const totalSelector = '[data-fill-look-total]';
const optionValueSelector = '[data-fill-look-option-value]';
const includeSelector = '[data-fill-look-include]';
const removeSelector = '[data-fill-look-remove]';
const stateKey = '__fillLookConfiguratorState';
const readyAttribute = 'data-fill-shop-the-look-ready';
const submittingAttribute = 'data-fill-look-submitting';
const defaultErrorMessage = "We couldn't add this look. Please try again.";
const selectOneMessage = 'Select at least one piece to continue.';

function parseJson(script) {
  try {
    return JSON.parse(script?.textContent || '{}');
  } catch (error) {
    console.warn('[fill-shop-the-look] Invalid configurator JSON', error);
    return {};
  }
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (character) => {
    const entities = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };

    return entities[character];
  });
}

function isDefaultOnlyProduct(product) {
  return (
    product.variants?.length === 1 &&
    (product.options?.length === 0 ||
      (product.options?.length === 1 &&
        product.options[0]?.name === 'Title' &&
        product.options[0]?.values?.[0] === 'Default Title'))
  );
}

function getPreferredSizeValue(option) {
  if (!/size/i.test(option?.name || '')) return null;

  return (
    option.values?.find((value) => {
      const normalizedValue = String(value).trim().toLowerCase();
      return normalizedValue === 'm' || normalizedValue === 'medium';
    }) || null
  );
}

function getInitialSelections(product) {
  if (!product.options?.length || isDefaultOnlyProduct(product)) {
    return product.variants?.[0]?.options ? [...product.variants[0].options] : [];
  }

  const selections = product.options.map(() => null);

  product.options.forEach((option, optionIndex) => {
    const preferredValue = getPreferredSizeValue(option);
    if (!preferredValue || !isValueAvailable(product, optionIndex, preferredValue, selections)) return;

    selections[optionIndex] = preferredValue;
  });

  return selections;
}

function findSelectedVariant(product, selections) {
  if (isDefaultOnlyProduct(product)) {
    const variant = product.variants?.[0];
    return variant?.available ? variant : null;
  }

  if (!product.options?.every((_, index) => selections[index])) return null;

  return (
    product.variants?.find((variant) => {
      return variant.available && variant.options?.every((option, index) => option === selections[index]);
    }) || null
  );
}

function getVariantForState(item) {
  return findSelectedVariant(item.product, item.selections);
}

function formatCents(cents, precision = 2, thousands = ',', decimal = '.') {
  const amount = (Number(cents) / 100).toFixed(precision);
  const [whole, fraction] = amount.split('.');
  const wholeWithSeparators = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

  return precision > 0 ? `${wholeWithSeparators}${decimal}${fraction}` : wholeWithSeparators;
}

function formatPrice(config, price) {
  const format = config.moneyFormat || '${{amount}}';
  const cents = Number(price) || 0;
  const placeholderMatch = format.match(/\{\{\s*(amount(?:_[a-z_]+)?)\s*\}\}/);
  const placeholder = placeholderMatch?.[0] || '{{amount}}';
  const placeholderName = placeholderMatch?.[1] || 'amount';
  let value;

  switch (placeholderName) {
    case 'amount_no_decimals':
      value = formatCents(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatCents(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatCents(cents, 0, '.', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = formatCents(cents, 0, ' ');
      break;
    case 'amount_with_space_separator':
      value = formatCents(cents, 2, ' ', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatCents(cents, 2, "'", '.');
      break;
    default:
      value = formatCents(cents);
  }

  return format.replace(placeholder, value);
}

function getCartItemsSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => element instanceof HTMLElement && element.dataset.sectionId)
    .filter(Boolean);
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function getErrorMessage(error) {
  const data = error?.data || error;

  if (typeof data?.description === 'string' && data.description.trim()) return data.description;
  if (typeof data?.message === 'string' && data.message.trim()) return data.message;
  if (typeof data?.error === 'string' && data.error.trim()) return data.error;

  if (data?.errors && typeof data.errors === 'object') {
    const firstError = Object.values(data.errors).flat().find(Boolean);
    if (typeof firstError === 'string' && firstError.trim()) return firstError;
  }

  if (typeof error?.message === 'string' && error.message.trim()) return error.message;

  return defaultErrorMessage;
}

async function parseCartResponse(response) {
  const text = await response.text();
  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch {
    if (!response.ok) throw new Error(defaultErrorMessage);
    return {};
  }
}

function getCartUrl(config) {
  return config.routes?.cartUrl || window.Theme?.routes?.cart_url || '/cart';
}

async function fetchCart(config) {
  const cartUrl = `${getCartUrl(config)}.json`;
  const response = await fetch(cartUrl, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  if (!response.ok) throw new Error(defaultErrorMessage);

  return response.json();
}

function setError(state, message) {
  state.errorElement.textContent = message || defaultErrorMessage;
  state.errorElement.hidden = false;
  state.statusElement.textContent = message || defaultErrorMessage;
}

function clearError(state) {
  state.errorElement.textContent = '';
  state.errorElement.hidden = true;
}

function setSubmitting(state, isSubmitting) {
  state.root.toggleAttribute(submittingAttribute, isSubmitting);
  state.submitButton.disabled = isSubmitting || state.selectedCount === 0;
  state.submitButton.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
  state.submitText.textContent = isSubmitting ? 'Adding...' : 'Add the Look';
}

function isValueAvailable(product, optionIndex, value, selections) {
  return product.variants?.some((variant) => {
    if (!variant.available || variant.options?.[optionIndex] !== value) return false;

    return variant.options.every((optionValue, index) => {
      return index === optionIndex || !selections[index] || selections[index] === optionValue;
    });
  });
}

function renderOptionControl(state, item, option, optionIndex) {
  const product = item.product;
  const selectedValue = item.selections[optionIndex];
  const values = option.values || [];
  const rowId = `${state.root.id}-product-${product.id}-option-${optionIndex}`;
  const disabled = item.included ? '' : ' disabled';

  if (values.length > 7) {
    return `
      <label class="fill-look-configurator__select-label" for="${rowId}">
        <span>${escapeHtml(option.name)}</span>
        <select
          id="${rowId}"
          class="fill-look-configurator__select"
          data-product-id="${escapeHtml(product.id)}"
          data-option-index="${optionIndex}"
          data-fill-look-option-value
          ${disabled}
        >
          <option value="">Choose ${escapeHtml(option.name)}</option>
          ${values
            .map((value) => {
              const available = isValueAvailable(product, optionIndex, value, item.selections);
              return `
                <option value="${escapeHtml(value)}"${selectedValue === value ? ' selected' : ''}${available ? '' : ' disabled'}>
                  ${escapeHtml(value)}${available ? '' : ' - unavailable'}
                </option>
              `;
            })
            .join('')}
        </select>
      </label>
    `;
  }

  return `
    <fieldset class="fill-look-configurator__option-group">
      <legend>${escapeHtml(option.name)}</legend>
      <div class="fill-look-configurator__option-values">
        ${values
          .map((value) => {
            const available = isValueAvailable(product, optionIndex, value, item.selections);
            const inputId = `${rowId}-${String(value).replace(/\W+/g, '-').toLowerCase()}`;

            return `
              <span class="fill-look-configurator__option-choice">
                <input
                  id="${inputId}"
                  class="fill-look-configurator__option-input"
                  type="radio"
                  name="${rowId}"
                  value="${escapeHtml(value)}"
                  data-product-id="${escapeHtml(product.id)}"
                  data-option-index="${optionIndex}"
                  data-fill-look-option-value
                  ${selectedValue === value ? ' checked' : ''}
                  ${available && item.included ? '' : ' disabled'}
                >
                <label class="fill-look-configurator__option-label" for="${inputId}">
                  <span>${escapeHtml(value)}</span>
                  ${available ? '' : '<span class="fill-look-configurator__unavailable-text">Unavailable</span>'}
                </label>
              </span>
            `;
          })
          .join('')}
      </div>
    </fieldset>
  `;
}

function renderRow(state, item) {
  const product = item.product;
  const variant = getVariantForState(item);
  const displayImage = variant?.featuredImage || product.featuredImage || '';
  const hasOptions = product.options?.length && !isDefaultOnlyProduct(product);
  const priceText = variant ? formatPrice(state.config, variant.price) : product.variants?.[0]?.priceFormatted || '';
  const rowClasses = [
    'fill-look-configurator__row',
    item.included ? '' : 'fill-look-configurator__row--removed',
    item.hasError ? 'fill-look-configurator__row--error' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return `
    <article class="${rowClasses}" data-fill-look-row data-product-id="${escapeHtml(product.id)}">
      <div class="fill-look-configurator__include">
        <input
          id="${state.root.id}-include-${escapeHtml(product.id)}"
          class="fill-look-configurator__include-input"
          type="checkbox"
          data-product-id="${escapeHtml(product.id)}"
          data-fill-look-include
          aria-label="${escapeHtml(item.included ? `Remove ${product.title} from this look` : `Include ${product.title} in this look`)}"
          ${item.included ? 'checked' : ''}
        >
        <label class="fill-look-configurator__include-label" for="${state.root.id}-include-${escapeHtml(product.id)}">
          <span>${item.included ? 'Included' : 'Include'}</span>
        </label>
      </div>

      <div class="fill-look-configurator__media" aria-hidden="true">
        ${displayImage ? `<img src="${escapeHtml(displayImage)}" alt="" loading="lazy">` : ''}
      </div>

      <div class="fill-look-configurator__details">
        <div class="fill-look-configurator__product-header">
          <div class="fill-look-configurator__product-copy">
            ${product.type ? `<p class="fill-look-configurator__type">${escapeHtml(product.type)}</p>` : ''}
            <h3 class="fill-look-configurator__product-title">
              <a href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a>
            </h3>
          </div>
          <p class="fill-look-configurator__price">${item.included && variant ? escapeHtml(priceText) : '-'}</p>
        </div>

        ${
          hasOptions
            ? `<div class="fill-look-configurator__options"${item.included ? '' : ' aria-disabled="true"'}>
                ${product.options.map((option, optionIndex) => renderOptionControl(state, item, option, optionIndex)).join('')}
              </div>`
            : `<p class="fill-look-configurator__auto-selected">${variant ? 'Variant selected automatically.' : 'Currently unavailable.'}</p>`
        }

        <div class="fill-look-configurator__row-actions">
          <button
            class="fill-look-configurator__remove"
            type="button"
            data-product-id="${escapeHtml(product.id)}"
            data-fill-look-remove
            aria-label="${escapeHtml(item.included ? `Remove ${product.title} from this look` : `Include ${product.title} in this look`)}"
          >
            ${item.included ? 'Remove' : 'Include'}
          </button>
        </div>
      </div>
    </article>
  `;
}

function updateSummary(state) {
  const includedItems = state.items.filter((item) => item.included);
  const selectedVariants = includedItems.map(getVariantForState).filter(Boolean);
  const total = selectedVariants.reduce((sum, variant) => sum + (Number(variant.price) || 0), 0);
  const piecesLabel = `${includedItems.length} ${includedItems.length === 1 ? 'piece' : 'pieces'} selected`;

  state.selectedCount = includedItems.length;
  state.countElement.textContent = includedItems.length === 0 ? selectOneMessage : piecesLabel;
  state.totalElement.textContent = formatPrice(state.config, total);
  state.submitButton.disabled = state.root.hasAttribute(submittingAttribute) || includedItems.length === 0;

  if (includedItems.length === 0) {
    setError(state, selectOneMessage);
  } else if (state.errorElement.textContent === selectOneMessage) {
    clearError(state);
  }
}

function renderRows(state) {
  state.rowsElement.innerHTML = state.items.map((item) => renderRow(state, item)).join('');
  updateSummary(state);
}

function resetState(state) {
  state.items = (state.config.components || []).map((product) => ({
    product,
    included: true,
    selections: getInitialSelections(product),
    hasError: false,
  }));
  state.selectedCount = state.items.length;
  clearError(state);
  setSubmitting(state, false);
  renderRows(state);
}

function getItemByProductId(state, productId) {
  return state.items.find((item) => String(item.product.id) === String(productId));
}

function handleOptionChange(state, control) {
  const item = getItemByProductId(state, control.dataset.productId);
  if (!item) return;

  const optionIndex = Number(control.dataset.optionIndex);
  item.selections[optionIndex] = control.value || null;
  item.hasError = false;
  clearError(state);
  renderRows(state);

  const productId = CSS.escape(String(item.product.id));
  const optionValue = CSS.escape(String(item.selections[optionIndex] || ''));
  const focusTarget =
    state.rowsElement.querySelector(
      `[data-product-id="${productId}"][data-option-index="${optionIndex}"][value="${optionValue}"]`
    ) || state.rowsElement.querySelector(`[data-product-id="${productId}"][data-option-index="${optionIndex}"]`);
  focusTarget?.focus?.({ preventScroll: true });
}

function setIncluded(state, productId, included) {
  const item = getItemByProductId(state, productId);
  if (!item) return;

  item.included = included;
  item.hasError = false;
  clearError(state);
  renderRows(state);

  const escapedProductId = CSS.escape(String(item.product.id));
  state.rowsElement
    .querySelector(`[data-product-id="${escapedProductId}"][data-fill-look-include]`)
    ?.focus?.({ preventScroll: true });
}

function validateState(state) {
  const includedItems = state.items.filter((item) => item.included);

  state.items.forEach((item) => {
    item.hasError = false;
  });

  if (includedItems.length === 0) {
    setError(state, selectOneMessage);
    return false;
  }

  for (const item of includedItems) {
    const variant = getVariantForState(item);
    if (variant) continue;

    item.hasError = true;
    const missingOption = item.product.options?.find((_, index) => !item.selections[index]);
    const message = missingOption
      ? `Please choose a ${missingOption.name.toLowerCase()} for ${item.product.title}.`
      : `${item.product.title} is currently unavailable.`;
    setError(state, message);
    renderRows(state);
    const row = state.rowsElement.querySelector(`[data-product-id="${CSS.escape(String(item.product.id))}"]`);
    row?.scrollIntoView({ block: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    row?.querySelector('input:not(:disabled), select:not(:disabled), button:not(:disabled)')?.focus({ preventScroll: true });
    return false;
  }

  clearError(state);
  return true;
}

function getCartLines(state) {
  return state.items
    .filter((item) => item.included)
    .map((item) => {
      const variant = getVariantForState(item);
      if (!variant) return null;

      return {
        id: Number(variant.id),
        quantity: 1,
        properties: {
          _look_id: `look-${state.config.look?.id || state.config.look?.handle}`,
          _look_name: state.config.look?.title || 'Shop the Look',
          _look_parent: state.config.look?.handle || String(state.config.look?.id || ''),
        },
      };
    })
    .filter(Boolean);
}

async function addLookToCart(state, items) {
  const sectionIds = getCartItemsSectionIds();
  const payload = {
    items,
    sections: sectionIds.join(','),
  };

  const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
    credentials: 'same-origin',
  });
  const data = await parseCartResponse(response);

  if (!response.ok || data?.status) {
    const error = new Error(getErrorMessage(data));
    error.data = data;
    throw error;
  }

  return data;
}

async function handleSubmit(state, event) {
  event.preventDefault();

  if (state.root.hasAttribute(submittingAttribute)) return;
  if (!validateState(state)) return;

  const items = getCartLines(state);
  if (!items.length) {
    setError(state, selectOneMessage);
    return;
  }

  const { CartErrorEvent, CartLinesUpdateEvent } = await import('@shopify/events');
  const deferredEvent = CartLinesUpdateEvent.createPromise();
  const eventDetail = {
    source: 'fill-look-configurator',
    sourceId: state.root.id,
    productId: state.config.look?.id,
    itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
  };

  state.dialog.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'dialog',
      lines: items.map((item) => ({
        merchandiseId: String(item.id),
        quantity: item.quantity,
      })),
      promise: deferredEvent.promise,
      detail: eventDetail,
    })
  );

  setSubmitting(state, true);
  clearError(state);

  try {
    const addResponse = await addLookToCart(state, items);
    const cart = await fetchCart(state.config);

    deferredEvent.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        ...eventDetail,
        items: cart.items,
        sections: addResponse.sections,
        didError: false,
      },
    });

    state.statusElement.textContent = 'Look added to your bag.';
    window.location.assign(getCartUrl(state.config));
  } catch (error) {
    deferredEvent.reject(error);
    state.dialog.dispatchEvent(
      new CartErrorEvent({
        error: getErrorMessage(error),
        code: 'INVALID',
        detail: error?.data,
      })
    );
    setError(state, getErrorMessage(error));
    setSubmitting(state, false);
  }
}

function bindConfigurator(root) {
  if (root[stateKey]) return root[stateKey];

  const config = parseJson(root.querySelector(dataSelector));
  const state = {
    root,
    config,
    dialog: root.querySelector('dialog'),
    rowsElement: root.querySelector(rowsSelector),
    form: root.querySelector(formSelector),
    errorElement: root.querySelector(errorSelector),
    statusElement: root.querySelector(statusSelector),
    submitButton: root.querySelector(submitSelector),
    submitText: root.querySelector(submitTextSelector),
    countElement: root.querySelector(countSelector),
    totalElement: root.querySelector(totalSelector),
    lastTrigger: null,
    selectedCount: 0,
    items: [],
  };

  if (
    !state.dialog ||
    !state.rowsElement ||
    !state.form ||
    !state.errorElement ||
    !state.statusElement ||
    !state.submitButton ||
    !state.submitText ||
    !state.countElement ||
    !state.totalElement
  ) {
    return null;
  }

  state.dialog.addEventListener('click', (event) => {
    if (event.target !== state.dialog) event.stopPropagation();
  });

  state.form.addEventListener('submit', (event) => handleSubmit(state, event));
  root.querySelector(closeSelector)?.addEventListener('click', () => root.closeDialog?.());

  state.rowsElement.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

    if (target.matches(includeSelector)) {
      setIncluded(state, target.dataset.productId, target.checked);
      return;
    }

    if (target.matches(optionValueSelector)) {
      handleOptionChange(state, target);
    }
  });

  state.rowsElement.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest(removeSelector) : null;
    if (!(button instanceof HTMLButtonElement)) return;

    const item = getItemByProductId(state, button.dataset.productId);
    if (item) setIncluded(state, item.product.id, !item.included);
  });

  root.addEventListener('dialog:close', () => {
    state.lastTrigger?.focus?.({ preventScroll: true });
    state.lastTrigger = null;
  });

  root[stateKey] = state;
  return state;
}

async function openConfigurator(root, trigger) {
  const state = bindConfigurator(root);
  if (!state) return;

  state.lastTrigger = trigger;
  resetState(state);

  await customElements.whenDefined('dialog-component');
  root.showDialog?.();

  requestAnimationFrame(() => {
    root.querySelector(closeSelector)?.focus?.({ preventScroll: true });
  });
}

function initSection(section) {
  if (section.getAttribute(readyAttribute) === 'true') return;
  section.setAttribute(readyAttribute, 'true');

  section.querySelectorAll(configuratorSelector).forEach(bindConfigurator);
  section.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest(openSelector) : null;
    if (!(trigger instanceof HTMLButtonElement)) return;

    const card = trigger.closest('.fill-shop-the-look__card');
    const configurator = card?.querySelector(configuratorSelector);
    if (!configurator) return;

    openConfigurator(configurator, trigger);
  });
}

function initAllSections(root = document) {
  root.querySelectorAll(sectionSelector).forEach(initSection);
}

document.addEventListener('shopify:section:load', (event) => {
  initAllSections(event.target);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAllSections(), { once: true });
} else {
  initAllSections();
}
